import React, { useState } from 'react';
import { useFormDepartureContext } from '../../../components/FormDepartureContext';
import CustomSelect from '../../../components/shared/CustomSelect';
import { SNL_BRAND } from '../../../data/map';

const SECTION_OPTIONS = ['1. Personal Information', '2. Ticket Information', '3. Date Information', '4. Upload Photos'];

const fmtDate = (iso) => {
  if (!iso) return 'Not specified';
  const [y, m, d] = iso.split('-');
  return d && m && y ? `${d}/${m}/${y}` : iso;
};

const Item = ({ label, value, span }) => (
  <div className="ob-record-item" style={span ? { gridColumn: '1 / -1' } : undefined}>
    <div className="ob-record-item-label">{label}</div>
    <div className="ob-record-item-value">{value || 'Not specified'}</div>
  </div>
);

const Card = ({ title, children }) => (
  <div className="ob-record-card" style={{ marginBottom: 20 }}>
    <div className="ob-record-header">
      <h3 className="ob-record-title">{title}</h3>
    </div>
    <div className="ob-record-grid">{children}</div>
  </div>
);

const ReviewSubmitSection = () => {
  const { formData, isSubmitting, submitMessage, setSubmitting, setSubmitMessage, setCurrentStep, resetForm, brand } = useFormDepartureContext();
  const ACCENT = (brand || SNL_BRAND).accent;

  const [uploadProgress, setUploadProgress] = useState(0);
  const [totalPhotos, setTotalPhotos] = useState(0);
  const [uploadedPhotos, setUploadedPhotos] = useState(0);
  const [jumpTo, setJumpTo] = useState('');

  const handleSectionSelect = (label) => {
    setJumpTo(label);
    const idx = SECTION_OPTIONS.indexOf(label);
    if (idx >= 0) setCurrentStep(idx);
  };

  const validateForm = () => {
    const errors = [];
    if (!formData.jobTitle?.trim()) errors.push('Ticket Title is required');
    if (!formData.jobType?.trim()) errors.push('Ticket Type is required');
    if (!formData.jobDescription?.trim()) errors.push('Ticket Description is required');
    if (!formData.completedDate) errors.push('Completed Date is required');
    if (!formData.completedTime) errors.push('Completed Time is required');
    if (!formData.photos || formData.photos.length === 0) errors.push('At least one photo is required');
    return errors;
  };

  // ── Client-side compression ─────────────────────────────────────────────
  // Phone photos are routinely 8–15 MB. Resize to max 1920px and re-encode as
  // JPEG (~0.5–1.5 MB) before upload. GIFs and already-small files pass through.
  const compressImage = (file, maxDim = 1920, quality = 0.82) =>
    new Promise((resolve) => {
      const ext = file.name.split('.').pop().toLowerCase();
      if (ext === 'gif' || file.size < 1.5 * 1024 * 1024) return resolve(file);
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        URL.revokeObjectURL(url);
        const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(
          (blob) => {
            if (!blob) return resolve(file);
            const name = file.name.replace(/\.[^.]+$/, '') + '.jpg';
            resolve(new File([blob], name, { type: 'image/jpeg' }));
          },
          'image/jpeg',
          quality
        );
      };
      img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
      img.src = url;
    });

  const withTimeout = (promise, ms, label) =>
    Promise.race([promise, new Promise((_, rej) => setTimeout(() => rej(new Error(`${label} timed out`)), ms))]);

  // ── One hop per photo: compress → upload to Xano → attach to the ticket ──
  const uploadAndAttachPhotos = async (recordId) => {
    const cleanRequestName = formData.requestName.replace(/[^a-zA-Z0-9]/g, '');
    const cleanCompany = formData.company.replace(/[^a-zA-Z0-9]/g, '');
    const cleanDate = formData.completedDate.replace(/[-/]/g, '');

    const validPhotos = (formData.photos || []).filter((p) => p?.file instanceof File && p.file.size > 0);
    setTotalPhotos(validPhotos.length);
    setUploadedPhotos(0);
    const failed = [];

    for (let i = 0; i < validPhotos.length; i++) {
      setUploadProgress(Math.round((i / validPhotos.length) * 100));
      setSubmitMessage({ type: 'info', message: `Uploading photo ${i + 1} of ${validPhotos.length}…` });
      try {
        const compressed = await compressImage(validPhotos[i].file);
        const extension = compressed.name.split('.').pop().toLowerCase();
        const renamed = new File(
          [compressed],
          `${cleanRequestName}_${cleanCompany}_${cleanDate}_photo${i + 1}.${extension}`,
          { type: compressed.type }
        );

        // Hop 1 — upload the bytes, get a public URL
        const up = new FormData();
        up.append('file', renamed);
        const upRes = await withTimeout(
          fetch('/api/upload-multiple-files', { method: 'POST', body: up, credentials: 'include' }),
          60000, `Photo ${i + 1} upload`
        );
        const upData = await upRes.json().catch(() => ({}));
        if (!upRes.ok || !upData.url) throw new Error(upData.message || `Upload failed (${upRes.status})`);

        // Hop 2 — attach that URL to the ticket record
        const atRes = await withTimeout(
          fetch(`/api/ticket/${recordId}/photo`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ url: upData.url, filename: renamed.name }),
          }),
          30000, `Photo ${i + 1} attach`
        );
        const atData = await atRes.json().catch(() => ({}));
        if (!atRes.ok || atData.status !== 'success') throw new Error(atData.message || `Attach failed (${atRes.status})`);

        setUploadedPhotos((c) => c + 1);
      } catch (err) {
        console.error(`Photo ${i + 1}:`, err);
        failed.push(`photo ${i + 1} (${err.message})`);
      }
    }
    setUploadProgress(100);
    return failed;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    const errors = validateForm();
    if (errors.length > 0) {
      setSubmitMessage({ type: 'error', message: errors.join('. ') });
      return;
    }
    setSubmitting(true);
    setSubmitMessage({ type: 'info', message: 'Creating your ticket…' });
    try {
      // 1. Create the ticket first (no photos) so nothing is lost if a photo fails
      const { photos, photoUpload, ...ticketFields } = formData;
      const response = await fetch('/api/submit-ticket', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(ticketFields),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.record?.id) throw new Error(data.message || 'Submission failed');
      const recordId = data.record.id;

      // 2. Photos one at a time
      const failed = await uploadAndAttachPhotos(recordId);

      if (failed.length === 0) {
        setSubmitMessage({ type: 'success', message: 'Ticket created and all photos attached! Starting a fresh ticket…' });
      } else {
        setSubmitMessage({
          type: 'error',
          message: `Ticket created, but ${failed.length} photo${failed.length > 1 ? 's' : ''} could not be attached: ${failed.join(', ')}.`,
        });
      }
      // Stay on the page: back to step 1 with Company / Request Name / Email still filled
      setTimeout(() => { resetForm(); }, failed.length ? 4500 : 2500);
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitMessage({ type: 'error', message: error.message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="form-section">
      <h4 className="ob-record-title" style={{ marginBottom: 12 }}>Review Summary</h4>

      <Card title="Personal Information">
        <Item label="Company" value={formData.company} />
        <Item label="Request Name" value={formData.requestName} />
        <Item label="Email" value={formData.email} span />
      </Card>

      <Card title="Ticket Information">
        <Item label="Ticket Title" value={formData.jobTitle} />
        <Item label="Ticket Type" value={formData.jobType} />
        <Item label="Description" value={formData.jobDescription} span />
      </Card>

      <Card title="Dates Information">
        <Item label="Completed Date" value={fmtDate(formData.completedDate)} />
        <Item label="Completed Time" value={formData.completedTime} />
      </Card>

      <Card title="Photos">
        {formData.photos?.length > 0 ? (
          formData.photos.map((photo, i) => (
            <div key={photo.id} className="ob-record-item" style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ width: 22, height: 22, borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                background: `${ACCENT}26`, color: ACCENT, fontSize: '0.7rem', fontWeight: 700 }}>{i + 1}</span>
              <span className="ob-record-item-value">{photo.name}</span>
            </div>
          ))
        ) : (
          <Item label="Photos" value="No photos uploaded" span />
        )}
      </Card>

      {isSubmitting && uploadProgress > 0 && (
        <div style={{ margin: '10px 0' }}>
          <div className="ob-subtitle" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
            <span>Uploading photos ({uploadedPhotos}/{totalPhotos})</span>
            <span>{uploadProgress}%</span>
          </div>
          <div style={{ width: '100%', height: 6, borderRadius: 999, background: 'rgba(255,255,255,0.08)' }}>
            <div style={{ width: `${uploadProgress}%`, height: '100%', borderRadius: 999, transition: 'width .3s ease',
              background: 'linear-gradient(90deg, #16a34a, #4ade80)' }} />
          </div>
        </div>
      )}

      {submitMessage && (
        <div className={`ob-toast ${submitMessage.type === 'error' ? 'ob-toast-error' : 'ob-toast-success'}`}
          style={{ position: 'static', margin: '10px 0', transform: 'none' }}>
          {submitMessage.message}
        </div>
      )}

      <div className="ob-divider" />

      <div className="ob-field" style={{ marginBottom: 20 }}>
        <CustomSelect label="Edit a section" value={jumpTo} onChange={handleSectionSelect} options={SECTION_OPTIONS} />
      </div>

      <button type="button" className="ob-btn ob-btn-submit" onClick={handleSubmit} disabled={isSubmitting}>
        {isSubmitting ? 'Submitting…' : 'Submit Ticket'}
      </button>
      <p className="ob-subtitle" style={{ textAlign: 'center', marginTop: 8 }}>
        Review all information or edit any section above before submitting
      </p>
    </div>
  );
};

export default ReviewSubmitSection;